<template>
	<div v-if="deviceTypeForm" class="py-8">
		<DeviceTypePropertiesForm
			:form="deviceTypeForm"
			:disabled="isReadOnly"
			@on-save="saveDeviceType"
			@on-delete="confirmDelete"
		/>
	</div>
</template>

<script>
import DeviceTypePropertiesForm from "@/Forms/DeviceType/DeviceTypePropertiesForm.vue";
import { mapStores } from "pinia";
import { useDeviceTypesStore } from "@/stores/deviceTypes.js";

export default {
	name: "DeviceTypePropertiesView",
	components: {DeviceTypePropertiesForm},
	data() {
		return {
			deviceTypeForm: undefined,
			readOnly: false,
		}
	},
	emits: [
		'on-save',
		'on-delete',
	],
	props: {
		type: {
			type: Object,
		},
	},
	computed: {
		...mapStores(useDeviceTypesStore),
		isReadOnly() {
			return this.readOnly;
		},
	},
	methods: {
		asReadOnlyForm(data) {
			return {
				...data,
				errors: {},
				processing: false,
				validate: () => {},
			};
		},
		async loadDeviceTypeForm(typeId) {
			this.deviceTypeForm = undefined;
			this.readOnly = false;

			if (!typeId) {
				return;
			}

			if (typeId === 'NEW') {
				this.deviceTypeForm = await this.deviceTypesStore.createDeviceType();
				return;
			}

			const deviceType = this.type ?? await this.deviceTypesStore.showDeviceType(typeId);
			if (this.$route.params.type !== typeId) {
				return;
			}

			if (this.isGlobalType(deviceType) && !this.$auth.hasPermission('update:all-device-types')) {
				this.readOnly = true;
				this.deviceTypeForm = this.asReadOnlyForm(deviceType);
				return;
			}

			this.deviceTypeForm = await this.deviceTypesStore.editDeviceType(typeId);
			if (this.$route.params.type !== typeId) {
				return;
			}
			this.deviceTypeForm.id = typeId;
		},
		async saveDeviceType(pendingImageFile = null) {
			console.log('saving device type');
			try {
				const response = await this.deviceTypeForm.submit();
				const recordId = response.data.id;
				this.$toast.add({
					severity: 'success',
					summary: this.$t('pages.admin.devices.device_types.device_type.properties.device_type_properties_view.device_type_saved'),
					life: 3000,
				});
				if (pendingImageFile) {
					try {
						const formData = new FormData();
						formData.append('image', pendingImageFile);
						const imageResponse = await axios.post(`device-types/${recordId}/image`, formData, {
							headers: { 'Content-Type': 'multipart/form-data' },
						});
						if (this.deviceTypeForm) {
							this.deviceTypeForm.image_url = imageResponse.data.image_url;
						}
						this.$toast.add({
							severity: 'success',
							summary: this.$t('pages.admin.devices.device_types.device_type.properties.device_type_properties_view.image_upload_success'),
							life: 3000,
						});
					} catch (imageError) {
						console.error('Image upload failed', imageError);
						this.$toast.add({
							severity: 'error',
							summary: this.$t('pages.admin.devices.device_types.device_type.properties.device_type_properties_view.image_upload_failed'),
							life: 3000,
						});
					}
				}
				this.$emit('on-save');
				this.$router.replace({params: {...this.$route.params, type: recordId}});
			} catch (error) {
				//
			}
		},
		confirmDelete() {
			this.$confirm.require({
				group: 'delete',
        message: this.$t('pages.admin.devices.device_types.device_type.properties.device_type_properties_view.are_you_sure_you_want_to_delete_this_device_type'),
        header: this.$t('pages.admin.devices.device_types.device_type.properties.device_type_properties_view.delete_device_type'),
				accept: () => {
					this.deleteDeviceType();
				},
				reject: () => {
					this.$toast.add({
            severity: 'info',
            summary: this.$t('common.cancelled'),
            detail: this.$t('pages.admin.devices.device_types.device_type.properties.device_type_properties_view.device_type_was_not_deleted'),
            life: 3000
          });
				}
			});
		},
		async deleteDeviceType() {
			try {
				if (this.$route.params.type !== 'NEW') {
					await this.deviceTypesStore.deleteDeviceType(this.$route.params.type);
					this.$emit('on-delete');
				}
				this.$router.push({name: 'admin.devices.types', params: {...this.$route.params, type: undefined}});
			} catch (error) {
				console.log(error);
			}
		},
    isGlobalType(deviceType) {
      return deviceType?.company_id == null
          && !deviceType?.company_ids?.length
          && !deviceType?.companies?.length;
    },
	},
	watch: {
		'$route.params.type': {
			async handler(newValue, oldValue) {
				await this.loadDeviceTypeForm(newValue);
			},
			immediate: true,
		}
	},
}
</script>

<style scoped>

</style>
